import React, { useState } from 'react';
import { PageHeaderType } from 'types/PageHeaderTypes';
import { TabBar } from './TabBar';
import SwitchButton from 'components/buttons/SwitchButton';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useTranslation } from 'react-i18next';
import { useTabBar } from './useTabBar';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { findSettingsMenuItem, findPreviousSettingsRoute } from 'components/settings/settingsNavigation';
import { useViewOnlyPackageRestriction, hasAnyAllowedRight } from 'services/permissions/permissionChecks';
import ModuleOrderLink from 'components/billing/ModuleOrderLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

const PageHeader: React.FC<PageHeaderType> = ({
    viewKey, title, switchButton, switchButtonApiUrl, explanationText, explanationVideoUrl,
    requiredFeature, counter, disabled, smallTitle, tabs = [], basePath, syncTabsWithUrl = false
}) => {
const { t } = useTranslation();
    const { activeFeatures, packageFeatures, allowedRights, environmentType } = useAuthContext();

    // Determine whether the view only package hides some menu items
    const viewOnlyRestricted = useViewOnlyPackageRestriction();
    const { isEnvironmentSelected } = useSettingsDivision();
    const history = useHistory();

    // Initialize the feature state from the active features when the switch is hidden
    const [pageHeaderFeatureIsActive, setPageHeaderFeatureIsActive] = useState(
        !switchButton || !requiredFeature || activeFeatures.includes(requiredFeature)
    )

    // Handle the feature switch toggle of the page header
    const handleSwitchToggle = (isOn: boolean) => {

        // Set the feature state to show or hide the tabs
        setPageHeaderFeatureIsActive(isOn);

        // Only navigate away when the feature of this settings page itself is switched off
        if (isOn || !requiredFeature || !basePath) {
            return;
        }

        // Get the menu item of this settings page to check if it disappears
        const menuItem = findSettingsMenuItem(basePath);
        if (!menuItem || menuItem.requiredFeature !== requiredFeature || menuItem.showWhenDisabled) {
            return;
        }

        // Navigate to the settings menu item above the disappearing item
        const previousRoute = findPreviousSettingsRoute(
            basePath,
            requiredFeature,
            packageFeatures,
            activeFeatures,
            allowedRights,
            environmentType,
            isEnvironmentSelected,
            viewOnlyRestricted
        );
        if (previousRoute) {
            history.push(previousRoute);
        }
    };

    // Filter the tabs based on their active feature, rights and division scope
    const activeTabs = tabs.filter(tab => {
        // Hide environment wide tabs when another division is selected
        if (tab.environmentWide && !isEnvironmentSelected) {
            return false;
        }

        // Hide tabs which require a right the user does not have
        if (tab.permissionCheck && !hasAnyAllowedRight(allowedRights, tab.permissionCheck)) {
            return false;
        }

        return !tab.activeFeature || activeFeatures.includes(tab.activeFeature);
    });

    const { tabLabel, tabComponent, activeTab } = useTabBar({
        tabs: activeTabs,
        viewKey,
        basePath,
        syncWithUrl: syncTabsWithUrl,
    });

    // Show the global settings notice when the active tab contains a global list
    const showGlobalListNotice = activeFeatures.includes('divisions') && activeTabs[activeTab]?.globalList;

    return (
        <>
            <div className='container-filled'>
                {/* Module cards get a smaller bottom margin than regular page headers */}
                <div className={`pageheader ${smallTitle ? 'module-card' : ''}`}>
                    <div className='row'>
                        {switchButton && requiredFeature && switchButtonApiUrl && isEnvironmentSelected && !disabled && (
                            <div className='switch-button-wrapper'>
                                <SwitchButton
                                    key={requiredFeature}
                                    featureSwitch={true}
                                    name={requiredFeature}
                                    apiUrl={switchButtonApiUrl}
                                    isDisabled={disabled}
                                    onToggle={handleSwitchToggle}
                                />
                            </div>
                        )}
                        {smallTitle ? (
                            <h5>{t(title)}</h5>
                        ) : (
                            <h3>{t(title)}</h3>
                        )}
                        {/* Show the optional counter of the page next to the title */}
                        {pageHeaderFeatureIsActive && counter}
                    </div>
                    {explanationText && (
                        <div className='col-100'>
                            <div className='col-70'>
                                <p className='explanation-text'>
                                    {t(explanationText)}
                                </p>
                            </div>
                            <div className='col-30'>
                                {explanationVideoUrl}
                            </div>
                        </div>
                    )}
                    {/* Show the order or upgrade link below the text for unavailable modules */}
                    {disabled && (
                        <ModuleOrderLink feature={requiredFeature} />
                    )}
                    {/* Show a notice when the active tab contains global settings for all divisions */}
                    {pageHeaderFeatureIsActive && showGlobalListNotice && (
                        <div className='global-settings-notice alert-warning'>
                            <FontAwesomeIcon icon={faCircleExclamation} />
                            <span>{t('nav.global_settings_notice')}</span>
                        </div>
                    )}
                    {/* Only render the tabs if the main feature of the page header is active: if the switch button is on */}
                    {pageHeaderFeatureIsActive && activeTabs.length > 1 && (
                        <ul className='tabbar'>
                            {tabLabel}
                        </ul>
                    )}
                </div>
            </div>
            {/* Only render the tabs if the main feature of the page header is active: if the switch button is on */}
            {pageHeaderFeatureIsActive && React.Children.count(tabComponent) > 0 && (
                <div style={{ paddingTop: "0.5rem" }}>
                    {tabComponent}
                </div>
            )}
        </>
    );
};

export default PageHeader;